import { ScrollingModule } from '@angular/cdk/scrolling';
import { CdkTableModule } from '@angular/cdk/table';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ErrorHandler, Injectable, isDevMode, NgModule, NgZone } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialogModule } from '@angular/material/dialog';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSortModule } from '@angular/material/sort';
import { MatStepperModule } from '@angular/material/stepper';
import { MatToolbarModule } from '@angular/material/toolbar';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AbcCoreModule } from '@barthauer/abc/core';
import { AbcGraphicsAdaptersOpenLayersModule } from '@barthauer/abc/graphics/adapters/open-layers';
import { UsersModule } from '@barthauer/abc/management/users';
import { AbcMessagesModule, LoadProtocolService } from '@barthauer/abc/messages';
import { AbcExpressionsEditorService } from '@barthauer/abc/queries/opath/expressions-editor';
import {
  AbcAutoCompleteInputModule,
  AbcDropDownBoxComponent,
  AbcNumberBoxComponent,
  AbcPopupComponent,
  AbcUiComponentsModule,
  _AbcButtonComponent,
  _AbcCheckBoxComponent,
  _AbcGridComponent,
  _AbcSplitterComponent,
  AbcTextBoxComponent,
  _AbcDashboardComponent,
} from '@barthauer/abc/ui-components';
import { BasysArenaModule } from '@barthauer/basys/arena';
import { BasysInterfacesCommonAsciiWizardsImportModule } from '@barthauer/basys/interfaces/common/ascii/wizards/import';
import { AbcInterfacesCommonAsciiWizardsImportModule } from '@barthauer/abc/interfaces/common/ascii/wizards/import';
import { GisExportModule } from '@barthauer/basys/interfaces/common/gis/wizards/export';
import { M150ExportModule } from '@barthauer/basys/interfaces/wastewater/m150/wizards/export';
import {
  DxActionSheetModule,
  DxButtonModule,
  DxCheckBoxModule,
  DxListModule,
  DxMenuModule,
  DxNumberBoxModule,
  DxProgressBarModule,
  DxScrollViewModule,
  DxSelectBoxModule,
  DxSliderModule,
  DxTemplateModule,
  DxTooltipModule,
} from 'devextreme-angular';
import { TableVirtualScrollModule } from 'ng-table-virtual-scroll';
import { FlexLayoutModule } from 'ngx-flexible-layout';
import { BasysMapFrameComponent } from './basys-map-frame.component';
import { BasysMapFrameService } from './basys-map-frame.service';
import { CacheConfigComponent } from './caching/cache-config/cache-config.component';
import { DocumentsComponent } from './documents/documents.component';
import { AddObjectComponent } from './manipulation/add-object/add-object.component';
import { GeometryEditorComponent } from './manipulation/geometry-editor/geometry-editor.component';
import { SelectOtAndGidComponent } from './manipulation/select-ot-and-gid/select-ot-and-gid.component';
import { NetworktracingComponent } from './networktracing/networktracing.component';
import { ObjectInfoPanelComponent } from './object-info/object-info-panel/object-info-panel.component';
import { ObjectInfoComponent } from './object-info/object-info/object-info.component';
import { CordovaService } from '@barthauer/abc/core';
import { DataService } from './services/data.service';
import { LibService } from './services/lib.service';
import { MapFrameSettingsService } from './services/map-frame-settings.service';
import { RoutingService } from './services/routing.service';
import { ContainerComponent } from './shared/container.component';
import { TableComponent } from './table/table.component';
import { DashboardComponent } from './dashboard/dashboard.component';
export * from './models/commonData.model';

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  constructor(private zone: NgZone) {}

  handleError(error: any) {
    // Check if it's an error from an HTTP response
    if (!(error instanceof HttpErrorResponse)) {
      error = error.rejection ?? error; // get the error object
    }
    const text = <string>error?.toString();
    if (
      text?.indexOf('QuotaExceededError') >= 0 ||
      text?.indexOf('Authorization') >= 0 ||
      (text?.indexOf('Observable, Promise, Array, or Iterable') >= 0 && !localStorage.getItem('access_token'))
    ) {
    } else if (error) {
      console.error(error);
    }
    if (isDevMode()) {
      //alert(error ?? "ERROR");
    }
  }
}

@NgModule({
  declarations: [
    BasysMapFrameComponent,
    ContainerComponent,
    TableComponent,
    ObjectInfoPanelComponent,
    ObjectInfoComponent,
    AddObjectComponent,
    DocumentsComponent,
    CacheConfigComponent,
    SelectOtAndGidComponent,
    NetworktracingComponent,
    GeometryEditorComponent,
    DashboardComponent,
  ],
  exports: [BasysMapFrameComponent],
  imports: [
    CommonModule,
    BrowserModule,
    BrowserAnimationsModule,
    AbcCoreModule,
    MatBottomSheetModule,
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatBadgeModule,
    MatSnackBarModule,
    MatExpansionModule,
    MatSelectModule,
    MatSortModule,
    MatTableModule,
    MatPaginatorModule,
    MatMenuModule,
    MatSidenavModule,
    MatDividerModule,
    MatListModule,
    MatCheckboxModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatGridListModule,
    MatProgressBarModule,
    MatChipsModule,
    MatSlideToggleModule,
    FlexLayoutModule,
    ScrollingModule,
    CdkTableModule,
    MatDialogModule,
    MatCardModule,
    FormsModule,
    ReactiveFormsModule,
    BasysArenaModule,
    AbcUiComponentsModule,
    AbcGraphicsAdaptersOpenLayersModule,
    TableVirtualScrollModule,
    AbcMessagesModule,
    _AbcGridComponent,
    AbcAutoCompleteInputModule,
    DxScrollViewModule,
    DxProgressBarModule,
    DxActionSheetModule,
    DxListModule,
    DxTemplateModule,
    DxButtonModule,
    MatStepperModule,
    MatRadioModule,
    UsersModule,
    M150ExportModule,
    GisExportModule,
    BasysInterfacesCommonAsciiWizardsImportModule,
    AbcInterfacesCommonAsciiWizardsImportModule,
    DxMenuModule,
    DxSelectBoxModule,
    DxCheckBoxModule,
    MatTabsModule,
    DxSliderModule,
    DxNumberBoxModule,
    DxTooltipModule,
    _AbcSplitterComponent,
    AbcPopupComponent,
    AbcDropDownBoxComponent,
    AbcNumberBoxComponent,
    _AbcCheckBoxComponent,
    _AbcButtonComponent,
    AbcTextBoxComponent,
    _AbcDashboardComponent,
  ],
  providers: [
    BasysMapFrameService,
    DataService,
    LibService,
    CordovaService,
    RoutingService,
    LoadProtocolService,
    MapFrameSettingsService,
    AbcExpressionsEditorService,
    // {
    //   provide: ErrorHandler,
    //   useClass: GlobalErrorHandler,
    // },
    {
      provide: 'ObjectSpaceMaster',
      useValue: 'BaSYS.Master',
    },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class BasysMapFrameModule {}
